import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ChevronRightIcon, PanelLeftIcon, SearchIcon } from 'components/icons';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Tooltip } from 'components/ui/tooltip';
import { FOCUS_RING, FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { EnvChip } from 'layout/NewDesignLayout/Sidebar/EnvChip';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { isAdminItemActive, useVisibleAdminGroups } from './adminItems';

const PORTAL_HOME = '/home';

type Props = {
    /** On the rail: the way back alone. */
    collapsed: boolean;
    /** Folds the open sidebar to the rail; its header, which holds the control elsewhere, is not drawn here. */
    onCollapse: () => void;
};

/**
 * The sidebar on an Admin page: back to the portal, the environment chip and the fold to the rail in its header, the
 * Admin heading and a box narrowing the pages by name, then the groups with the pages the user may open. The list is
 * the part that scrolls.
 */
function AdminNavigation({ collapsed, onCollapse }: Props) {
    const { pathname } = useLocation();
    const groups = useVisibleAdminGroups();
    const [query, setQuery] = useState('');
    const backLabel = t('menu.back-to-portal');
    const findLabel = t('admin.find-setting');

    if (collapsed) {
        return (
            <div className="flex min-h-0 flex-1 flex-col items-center">
                <Tooltip content={backLabel} side="right">
                    <Link
                        to={PORTAL_HOME}
                        aria-label={backLabel}
                        className={cn(
                            'hover:bg-sidebar-hover flex size-9 items-center justify-center rounded-md',
                            FOCUS_RING
                        )}
                    >
                        <ChevronRightIcon className="size-[17px] rotate-180" />
                    </Link>
                </Tooltip>
            </div>
        );
    }

    const needle = query.trim().toLocaleLowerCase();
    const shown = groups
        .map((group) => ({
            ...group,
            items: group.items.filter((item) => t(item.labelId).toLocaleLowerCase().includes(needle)),
        }))
        .filter((group) => group.items.length > 0);

    // Fades as a whole while the sidebar collapses to the rail or expands from it.
    return (
        <div className="sidebar-fade flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-2 pr-3 pl-4">
                <Link
                    to={PORTAL_HOME}
                    className={cn(
                        'hover:bg-sidebar-hover mr-auto flex h-9 items-center gap-2 rounded-md pr-2.5 pl-1.5 font-medium',
                        FOCUS_RING
                    )}
                >
                    <ChevronRightIcon className="size-4 rotate-180" />
                    {backLabel}
                </Link>
                <EnvChip />
                <button
                    type="button"
                    aria-label={t('collapse')}
                    onClick={onCollapse}
                    className={cn(
                        'hover:bg-sidebar-hover flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
                        FOCUS_RING
                    )}
                >
                    <PanelLeftIcon className="size-[18px]" />
                </button>
            </div>
            <div className="flex shrink-0 flex-col gap-3 px-4 pt-1 pb-3">
                <h2 className="text-h4 text-primary-strong px-1.5">{t('menu.admin')}</h2>
                <InputGroup className="bg-paper border-sidebar-search-border shadow-none">
                    <InputGroupAddon>
                        <SearchIcon />
                    </InputGroupAddon>
                    <InputGroupInput
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={findLabel}
                        aria-label={findLabel}
                    />
                </InputGroup>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-col gap-3.5 px-4 pb-3">
                    {shown.map((group) => {
                        const headingId = `admin-${group.id}-heading`;
                        return (
                            <div key={group.id} className="flex flex-col">
                                <div id={headingId} className="text-label text-sidebar-muted px-1.5 pb-1 uppercase">
                                    {t(group.labelId)}
                                </div>
                                <ul aria-labelledby={headingId} className="flex flex-col gap-px">
                                    {group.items.map((item) => {
                                        const active = isAdminItemActive(item, pathname);
                                        return (
                                            <li key={item.id}>
                                                <Link
                                                    to={item.path}
                                                    aria-current={active ? 'page' : undefined}
                                                    className={cn(
                                                        'flex h-[30px] items-center rounded-[6px] px-1.5',
                                                        FOCUS_RING_INSET,
                                                        active
                                                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold'
                                                            : 'hover:bg-sidebar-hover'
                                                    )}
                                                >
                                                    {t(item.labelId)}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default AdminNavigation;
